/* __next_internal_client_entry_do_not_use__ TopOfPageContentWrapper auto */ import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { getWindow, getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { getSpacing } from "@faire/web--source/slate/spacing/getSpacing";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import { route as brandbrandTokenRoute } from "@faire/web-api--source/routes/brand/brandToken";
import { route as flashSaleRoute } from "@faire/web-api--source/routes/flash-sale";
import { route as posHomeRoute } from "@faire/web-api--source/routes/point-of-sale";
import { route as posAboutRoute } from "@faire/web-api--source/routes/point-of-sale/about";
import classNames from "classnames";
import React, { Suspense, useCallback, useEffect, useMemo, useRef } from "react";
import { matchPath } from "react-router-dom";
import { useZustandLocation } from "@faire/retailer-visitor-shared/app/_lib/routing/useZustandLocation";
import { isIdentityVerificationModalOrRoute } from "@faire/retailer/components/App/AppModals/utils";
import { useShouldShowSaleAnnouncementNudge } from "@faire/retailer/components/Header/desktop/SaleAnnouncementNudge/useShouldShowSaleAnnouncementNudge";
import { useNotificationPreview } from "@faire/retailer/components/Header/HeaderNotifications/hooks/useNotificationPreview";
import { useNotificationRefetch } from "@faire/retailer/components/Header/HeaderNotifications/hooks/useNotificationRefetch";
import { HeaderPriorityProvider, useHeaderPriorityStore } from "@faire/retailer/components/HeaderPriorityRenderer/HeaderPriorityPriovider";
import { LazyToastContainer } from "@faire/retailer/components/Toast";
import { AppNudgeProvider } from "@faire/retailer-visitor-shared/features/Nudges/useAppNudgeStore";
import { usePOSSummary } from "@faire/retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { isMobileApp } from "@faire/retailer-visitor-shared/lib/isMobileApp";
import { assignInlineVars } from "@faire/retailer/lib/tailwindUtils";
import { useStickyNavContentPositions } from "@faire/retailer/navbar/useStickyNavContentPositions";
import { useMarketStore } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { usePromoBanners } from "@faire/retailer-visitor-shared/stores/ui/usePromoBanners";
import { useTopOfPageContent } from "@faire/retailer-visitor-shared/stores/ui/useTopOfPageContent";
var HIDE_COMPONENT_OFFSET_TOP = 300;
var topVar = {
    variable: "--f-topc-top",
    className: "top-[--f-topc-top]"
};
var transitionDurationVar = {
    variable: "--f-topc-transition-duration",
    className: "duration-[--f-topc-transition-duration]"
};
var toastTopVar = {
    variable: "--f-topc-toast-top",
    className: "top-[--f-topc-toast-top]"
};
export var TopOfPageContentWrapper = function(param) {
    var children = param.children, hasNoTopBanner = param.hasNoTopBanner, hasToastContainer = param.hasToastContainer;
    useNotificationRefetch();
    var notificationPreview = useNotificationPreview();
    return /*#__PURE__*/ _jsx(HeaderPriorityProvider, {
        value: {
            isNotificationPreviewVisible: !!notificationPreview
        },
        children: /*#__PURE__*/ _jsx(TopOfPageContentWrapperBase, {
            hasNoTopBanner: hasNoTopBanner,
            hasToastContainer: hasToastContainer,
            children: children
        })
    });
};
var TopOfPageContentWrapperBase = function(param) {
    var children = param.children, hasNoTopBanner = param.hasNoTopBanner, hasToastContainer = param.hasToastContainer;
    var _useViewport = useViewport(), isDesktopAndAbove = _useViewport.isDesktopAndAbove, isMobile = _useViewport.isMobile;
    var contentRef = useRef(null);
    var resizeObserverRef = useRef(null);
    var shouldRetainOriginalPosition = useStickyNavContentPositions().shouldRetainOriginalPosition;
    var shouldShowSaleAnnouncementTooltip = useShouldShowSaleAnnouncementNudge();
    var _useHeaderPriorityStore = _sliced_to_array(useHeaderPriorityStore([
        "isNotificationPreviewVisible"
    ]), 1), isNotificationPreviewVisible = _useHeaderPriorityStore[0].isNotificationPreviewVisible;
    var shouldHideTopNav = useIsLoggedInRetailer();
    var setHeaderHeight = useTopOfPageContent().setHeaderHeight;
    var bannerHeight = usePromoBanners().bannerHeight;
    var _useMarketStore = useMarketStore([
        "isShowingCountdownBanner"
    ]), isShowingMarketCountdownBanner = _useMarketStore.isShowingCountdownBanner;
    var notificationPreview = useNotificationPreview();
    var hasLinkedPOS = usePOSSummary().hasLinkedPOS;
    var _useZustandLocation = useZustandLocation(function(state) {
        return state.pathname;
    }), pathnameWithMaybeLocale = _useZustandLocation.pathname;
    var pathname = useMemo(function() {
        return removeLocaleFromUrl(pathnameWithMaybeLocale);
    }, [
        pathnameWithMaybeLocale
    ]);
    var nonStickyHeader = useMemo(function() {
        var nonStickyHeaderPath = [
            {
                path: brandbrandTokenRoute,
                exact: true
            },
            {
                path: flashSaleRoute,
                exact: true
            },
            {
                path: posAboutRoute,
                exact: true
            }
        ].concat(_to_consumable_array(!hasLinkedPOS ? [
            {
                path: posHomeRoute,
                exact: true
            }
        ] : [])).filter(isNotUndefined);
        return nonStickyHeaderPath.some(function(path) {
            return matchPath(pathname, path) !== null;
        });
    }, [
        pathname,
        hasLinkedPOS
    ]);
    var handleResize = useCallback(function() {
        var _contentRef_current;
        var _contentRef_current_clientHeight;
        setHeaderHeight((_contentRef_current_clientHeight = (_contentRef_current = contentRef.current) === null || _contentRef_current === void 0 ? void 0 : _contentRef_current.clientHeight) !== null && _contentRef_current_clientHeight !== void 0 ? _contentRef_current_clientHeight : 0);
    }, [
        setHeaderHeight
    ]);
    var debouncedHandleResize = useMemo(function() {
        return debounceIfPossible(function() {
            handleResize();
        }, 500);
    }, [
        handleResize
    ]);
    useEffect(function() {
        var _getWindow;
        (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.addEventListener("resize", debouncedHandleResize);
        return function() {
            var _getWindow;
            (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.removeEventListener("resize", debouncedHandleResize);
        };
    }, [
        debouncedHandleResize
    ]);
    useEffect(function() {
        if (!shouldHideTopNav || nonStickyHeader) {
            return;
        }
        if (shouldRetainOriginalPosition) {
            var _contentRef_current;
            var _contentRef_current_clientHeight;
            setHeaderHeight((_contentRef_current_clientHeight = (_contentRef_current = contentRef.current) === null || _contentRef_current === void 0 ? void 0 : _contentRef_current.clientHeight) !== null && _contentRef_current_clientHeight !== void 0 ? _contentRef_current_clientHeight : 0);
        } else {
            setHeaderHeight(0);
        }
    }, [
        shouldHideTopNav,
        shouldRetainOriginalPosition,
        nonStickyHeader,
        setHeaderHeight
    ]);
    useEffect(function() {
        return function() {
            var _resizeObserverRef_current;
            if (contentRef.current) {
                var _resizeObserverRef_current1;
                (_resizeObserverRef_current1 = resizeObserverRef.current) === null || _resizeObserverRef_current1 === void 0 ? void 0 : _resizeObserverRef_current1.unobserve(contentRef.current);
            }
            (_resizeObserverRef_current = resizeObserverRef.current) === null || _resizeObserverRef_current === void 0 ? void 0 : _resizeObserverRef_current.disconnect();
            resizeObserverRef.current = null;
            contentRef.current = null;
        };
    }, []);
    var contentNodeCallback = useCallback(function(node) {
        if (node) {
            var observer = getWindowOrThrow().ResizeObserver ? new (getWindowOrThrow()).ResizeObserver(function(entry) {
                getWindowOrThrow().requestAnimationFrame(function() {
                    var _entry_;
                    if (!entry) {
                        return;
                    }
                    var _entry__contentRect_height;
                    setHeaderHeight((_entry__contentRect_height = (_entry_ = entry[0]) === null || _entry_ === void 0 ? void 0 : _entry_.contentRect.height) !== null && _entry__contentRect_height !== void 0 ? _entry__contentRect_height : 0);
                });
            }) : undefined;
            observer === null || observer === void 0 ? void 0 : observer.observe(node);
            contentRef.current = node;
            setHeaderHeight(contentRef.current.clientHeight);
            if (observer) {
                resizeObserverRef.current = observer;
            }
        }
    }, [
        setHeaderHeight
    ]);
    var style = useMemo(function() {
        if (!nonStickyHeader) {
            var _contentRef_current;
            // AddedToBag must not get unmounted when the `hideHeadersAndBanners` prop
            // changes, or otherwise we don't correctly show the added to bag toast
            // after adding a product to cart from the mobile product modal.
            var topPosition = isDesktopAndAbove && isShowingMarketCountdownBanner && !hasNoTopBanner ? bannerHeight : 0;
            // Preview has an absolute position, so we need to account for it
            var notificationPreviewOffset = notificationPreview && isNotificationPreviewVisible ? 100 : 0;
            var salesTooltipOffset = shouldShowSaleAnnouncementTooltip ? 188 : 0;
            var absoluteTopElementsOffset = notificationPreviewOffset + salesTooltipOffset;
            var stickyContentHeight = ((_contentRef_current = contentRef.current) === null || _contentRef_current === void 0 ? void 0 : _contentRef_current.clientHeight) ? contentRef.current.clientHeight + absoluteTopElementsOffset : undefined;
            var hideComponentOffsetTop = stickyContentHeight !== null && stickyContentHeight !== void 0 ? stickyContentHeight : HIDE_COMPONENT_OFFSET_TOP;
            var _obj;
            return assignInlineVars((_obj = {}, _define_property(_obj, topVar.variable, shouldRetainOriginalPosition ? "".concat(topPosition, "px") : "-".concat(hideComponentOffsetTop, "px")), _define_property(_obj, transitionDurationVar.variable, "".concat(hideComponentOffsetTop / HIDE_COMPONENT_OFFSET_TOP * 400, "ms")), _define_property(_obj, toastTopVar.variable, shouldRetainOriginalPosition ? "".concat(topPosition + hideComponentOffsetTop, "px") : "0px"), _obj));
        }
        return undefined;
    }, [
        bannerHeight,
        hasNoTopBanner,
        isDesktopAndAbove,
        isNotificationPreviewVisible,
        isShowingMarketCountdownBanner,
        nonStickyHeader,
        notificationPreview,
        shouldRetainOriginalPosition,
        shouldShowSaleAnnouncementTooltip
    ]);
    if (isMobileApp() && isIdentityVerificationModalOrRoute()) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(AppNudgeProvider, {
                children: /*#__PURE__*/ _jsx("div", {
                    "data-test-id": "header-content",
                    ref: contentNodeCallback,
                    className: nonStickyHeader ? "relative z-[12]" : classNames("transition-top sticky z-[12]", topVar.className, transitionDurationVar.className, shouldRetainOriginalPosition ? "ease-[cubic-bezier(0.1,0.7,0.6,1)]" : "ease-linear"),
                    style: style,
                    children: children
                })
            }),
            hasToastContainer ? /*#__PURE__*/ _jsx("div", {
                className: classNames("sticky z-[12] h-0", toastTopVar.className),
                style: style,
                children: /*#__PURE__*/ _jsx(Suspense, {
                    fallback: null,
                    children: /*#__PURE__*/ _jsx(LazyToastContainer, {
                        position: isMobile ? "bottom-center" : "top-right",
                        style: isMobile ? undefined : {
                            position: "absolute",
                            top: "initial",
                            bottom: getSpacing("-3x"),
                            right: getSpacing("3x"),
                            left: "auto",
                            width: "fit-content",
                            height: "fit-content",
                            padding: "0",
                            zIndex: 11,
                            maxWidth: "325px",
                            transform: "translateY(100%)"
                        }
                    })
                })
            }) : null
        ]
    });
};
